import { FC, ReactElement, ReactNode, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { cn, FaIcon } from '@fe-learner-common';

export interface SheetProps extends Dialog.DialogProps {
  children: ReactNode;
  sheetClassName?: string;
  trigger: ReactElement;
  open?: boolean;
  slideDirection?: 'ltr' | 'rtl';
  onOpenChange?: (open: boolean) => void;
  onCloseAutoFocus?: (event: Event) => void;
  headless?: boolean;
  preventDismiss?: boolean;
  container?: HTMLElement;
  texts?: {
    title: string;
    closeAriaLabel: string;
  };
}

const directionMap = {
  rtl: 'lf:data-[state=closed]:animate-sheetContentSlideOutRtl lf:data-[state=open]:animate-sheetContentSlideInRtl lf:rtl:data-[state=closed]:animate-sheetContentSlideOut lf:rtl:data-[state=open]:animate-sheetContentSlideIn',
  ltr: 'lf:data-[state=closed]:animate-sheetContentSlideOut lf:data-[state=open]:animate-sheetContentSlideIn lf:rtl:data-[state=closed]:animate-sheetContentSlideOutRtl lf:rtl:data-[state=open]:animate-sheetContentSlideInRtl',
};

export const Sheet: FC<SheetProps> = ({
  children,
  sheetClassName,
  texts,
  slideDirection = 'ltr',
  onCloseAutoFocus,
  trigger,
  container,
  headless = false,
  preventDismiss = false,
  ...rest
}) => {
  const blockAutoFocus = useRef(true);

  return (
    <Dialog.Root
      {...rest}
      onOpenChange={(open) => {
        if (open) {
          blockAutoFocus.current = true;
        }
        rest.onOpenChange && rest.onOpenChange(open);
      }}
    >
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal container={container}>
        <Dialog.Overlay className="lf:fixed lf:inset-0 lf:bg-black lf:opacity-[10%] lf:data-[state=open]:animate-sheetOverlayShow" />
        <Dialog.Content
          className={cn(
            'lf:fixed lf:bottom-0 lf:end-0 lf:top-0 lf:mt-16 lf:flex lf:w-full lf:flex-col lf:bg-neutral-0 lf:shadow-100',
            directionMap[slideDirection],
            sheetClassName,
          )}
          onEscapeKeyDown={(event) => {
            blockAutoFocus.current = false;

            if (preventDismiss) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            blockAutoFocus.current = false;

            if (preventDismiss) {
              event.preventDefault();
            }
          }}
          onCloseAutoFocus={(event) => {
            if (blockAutoFocus.current) {
              onCloseAutoFocus && onCloseAutoFocus(event);
            }
          }}
        >
          {headless ? null : (
            <div className="lf:flex lf:items-center lf:justify-between lf:border-b-1 lf:border-neutral-100 lf:p-3 lf:ps-6">
              <Dialog.Title asChild>
                <h2 className="lf:text-title-md">{texts?.title}</h2>
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className="lf:l-btn lf:l-icon-btn lf:l-btn-md lf:l-btn-tertiary"
                  id="closeSheet"
                  key="closeSheet"
                  aria-label={texts?.closeAriaLabel}
                >
                  <FaIcon width={20} height={20} icon={faXmark} />
                </button>
              </Dialog.Close>
            </div>
          )}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
